import { DEFAULT_OPTION, useAppForm } from '@massilia/features'
import { useUpsertMyIdentityDocumentMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { identityDocumentSchema } from '@/lib/schemas/personal-informations/personal-informations.schemas'

interface IdentityDocumentFormProps {
  passengerId: string
  onClose: () => void
}

const DOC_TYPE_OPTIONS = [
  { label: "Carte d'identité nationale", value: 'CNI' },
  { label: 'Passeport', value: 'PASSPORT' },
] as const

export function IdentityDocumentForm({ passengerId, onClose }: IdentityDocumentFormProps) {
  const { mutateAsync: upsertDocument } = useUpsertMyIdentityDocumentMutation()

  const form = useAppForm({
    defaultValues: {
      documentNumber: '',
      expiryDate: null as null | string,
      issuingCountry: DEFAULT_OPTION,
      type: 'PASSPORT' as 'CNI' | 'PASSPORT',
    },
    onSubmit: async ({ value }) => {
      try {
        await upsertDocument({
          passengerId,
          documentNumber: value.documentNumber,
          expiryDate: value.expiryDate ?? '',
          issuingCountry: value.issuingCountry.value,
          type: value.type,
        })
        onClose()
      } catch (error) {
        toast.error(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          identityDocumentSchema
        ),
    },
  })

  return (
    <form
      className="mt-3 flex flex-col gap-3 rounded-lg border border-slate-200 p-3"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <form.AppField
        name="type"
        children={(field) => (
          <field.RadioGroupField
            label="Type de document"
            options={DOC_TYPE_OPTIONS}
            orientation="horizontal"
          />
        )}
      />
      <form.AppField
        name="documentNumber"
        children={(field) => <field.TextField label="Numéro" placeholder="Numéro du document" />}
      />
      <form.AppField
        name="issuingCountry"
        children={(field) => <field.CountryAutocompleteField label="Pays de délivrance" />}
      />
      <form.AppField
        name="expiryDate"
        children={(field) => <field.DateField label="Date d'expiration" />}
      />
      <div className="flex gap-2">
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <>
              <Button
                className="bg-brand-600 hover:bg-brand-700 text-white"
                disabled={isSubmitting}
                size="sm"
                type="submit"
              >
                {isSubmitting ? 'Ajout...' : 'Ajouter'}
              </Button>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                size="sm"
                type="button"
                variant="outline"
              >
                Annuler
              </Button>
            </>
          )}
        </form.Subscribe>
      </div>
    </form>
  )
}
